import { Checkbox, ControlLabel, DropdownItem } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import isObject from 'lodash/isObject'
import ActionButtons from 'components/Filters/ActionButtons'
import styles from 'components/Filters/FilterPanel/subcomponents/Dropdown/Dropdown.module.scss'
import isEqual from 'lodash/isEqual'

const CheckboxGroupFilter = ({
  name,
  label,
  onApply = () => {},
  showActionButtons = true,
  onSelect = () => {},
  value = [],
  options = [],
  defaultValue = [],
  optionLabelPrefix = '',
  lock = false,
  rowRenderComponent = null,
}) => {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(value)
  const [focusedIndex, setFocusedIndex] = useState(0)
  const itemRefs = useRef([])

  useEffect(() => {
    !isEqual(checked, value) && setChecked(() => value)
  }, [value])

  useEffect(() => {
    onSelect(checked)
  }, [checked])

  const toggle = useCallback(
    (toggledValue) => {
      if (lock) return

      if (checked.includes(toggledValue)) {
        setChecked((prevState) => prevState.filter((checkboxItem) => checkboxItem !== toggledValue))
      } else {
        setChecked((prevState) => [...prevState, toggledValue])
      }
    },
    [checked, setChecked, lock],
  )

  const applyValue = useCallback(() => onApply({ fieldName: name, value: checked }), [checked])

  const dropFilter = useCallback(() => setChecked(defaultValue), [setChecked])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      if (!options.length) return
      e.preventDefault()
      const dir = e.key === 'ArrowDown' ? 1 : -1
      const next = (focusedIndex + dir + options.length) % options.length
      setFocusedIndex(next)
      const input = itemRefs.current[next]?.querySelector('input')
      input?.focus()
    },
    [focusedIndex, options.length],
  )

  return (
    <fieldset
      className={styles['checkbox-group']}
      disabled={lock}
      onKeyDown={handleKeyDown}
    >
      <legend className={styles['checkbox-group__legend']}>{label || name}</legend>
      <div className={styles['dropdown__scroll']}>
        {options.map((option, index) => {
          const { label: optionLabel, value } = isObject(option)
            ? option
            : { label: t(optionLabelPrefix + option), value: option }

          return (
            <div
              key={name + '-option-' + value}
              ref={(el) => {
                itemRefs.current[index] = el
              }}
            >
              <DropdownItem size={'l'}>
                <ControlLabel
                  onChange={() => {
                    toggle(value)
                  }}
                  checked={checked.includes(value)}
                  value={value}
                  name={name}
                  control={<Checkbox />}
                  tabIndex={index === focusedIndex ? 0 : -1}
                  label={
                    rowRenderComponent
                      ? rowRenderComponent({ ...(isObject(option) ? option : {}), label: optionLabel, value })
                      : optionLabel
                  }
                />
              </DropdownItem>
            </div>
          )
        })}
      </div>

      <ActionButtons
        dropFilter={dropFilter}
        applyValue={applyValue}
        showActionButtons={showActionButtons}
        disabledApplyAction={isEqual(checked, value)}
      />
    </fieldset>
  )
}

CheckboxGroupFilter.propTypes = {
  rowRenderComponent: PropTypes.func,
  label: PropTypes.string,
  name: PropTypes.string,
  onApply: PropTypes.func,
  showActionButtons: PropTypes.bool,
  onSelect: PropTypes.func,
  value: PropTypes.array,
  defaultValue: PropTypes.any,
  optionLabelPrefix: PropTypes.string,
  options: PropTypes.array,
  lock: PropTypes.bool,
}

export default CheckboxGroupFilter
